import { z } from 'zod'

/**
 * SSL, services, security and backups (PROMPT.md §5.9). Everything here is read from the server
 * and shown; the only things with a button are the ones §5.9 names explicitly.
 */

// ---------------------------------------------------------------- certificates

/** §5.9: under 21 days yellow, under 7 red. */
export const CERT_WARN_DAYS = 21
export const CERT_CRITICAL_DAYS = 7

export const certificateSchema = z.object({
  /** The letsencrypt lineage directory, which is usually the primary domain. */
  name: z.string(),
  path: z.string(),
  /** Every name the certificate is valid for. */
  domains: z.array(z.string()),
  issuer: z.string().nullable(),
  notAfter: z.iso.datetime().nullable(),
  daysLeft: z.number().nullable(),
  /** What the domain resolves to, and whether that is this server. */
  dns: z
    .object({
      addresses: z.array(z.string()),
      matchesServer: z.boolean(),
      detail: z.string(),
    })
    .nullable(),
})
export type Certificate = z.infer<typeof certificateSchema>

export const certificateListSchema = z.object({
  serverId: z.string(),
  /** False when the server has no certbot and no letsencrypt directory. */
  available: z.boolean(),
  /** Set when the list could not be read at all. */
  error: z.string().nullable(),
  certificates: z.array(certificateSchema),
})

export type CertificateList = z.infer<typeof certificateListSchema>

export const certbotActionSchema = z.object({
  action: z.enum(['dry-run', 'renew']),
  sudoPassword: z.string().optional(),
})

export const commandOutcomeSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
  exitCode: z.number().int().nullable(),
  output: z.string(),
})
export type CommandOutcome = z.infer<typeof commandOutcomeSchema>

// ---------------------------------------------------------------- services

export const serviceKindSchema = z.enum(['nginx', 'postgres', 'mysql', 'redis'])
export type ServiceKind = z.infer<typeof serviceKindSchema>

export const serviceDetailSchema = z.object({
  label: z.string(),
  value: z.string(),
})

export const serviceStatusSchema = z.object({
  kind: serviceKindSchema,
  /** The systemd unit, when there is one, so start/stop can go through that adapter. */
  unit: z.string().nullable(),
  running: z.boolean(),
  version: z.string().nullable(),
  /** Connection counts, database sizes, memory — whatever that service can say. */
  details: z.array(serviceDetailSchema),
  /** Only nginx: the result of `nginx -t`, which the reload button depends on. */
  configOk: z.boolean().nullable(),
  configOutput: z.string().nullable(),
  error: z.string().nullable(),
})
export type ServiceStatus = z.infer<typeof serviceStatusSchema>

export const serviceListSchema = z.object({
  serverId: z.string(),
  services: z.array(serviceStatusSchema),
})

export const nginxReloadSchema = z.object({
  sudoPassword: z.string().optional(),
})

// ---------------------------------------------------------------- updates

export const updatePackageSchema = z.object({
  name: z.string(),
  /** The version apt would install; null is never produced today, kept for forward safety. */
  version: z.string().nullable(),
  security: z.boolean(),
})
export type UpdatePackage = z.infer<typeof updatePackageSchema>

export const updatePreviewSchema = z.object({
  serverId: z.string(),
  /** False when apt is not the package manager here — the UI hides the whole section then. */
  available: z.boolean(),
  pending: z.number().int().nonnegative(),
  security: z.number().int().nonnegative(),
  packages: z.array(updatePackageSchema),
  rebootRequired: z.boolean(),
})
export type UpdatePreview = z.infer<typeof updatePreviewSchema>

export const updateScopeSchema = z.enum(['security', 'all'])
export type UpdateScope = z.infer<typeof updateScopeSchema>

export const runUpdatesSchema = z.object({
  scope: updateScopeSchema,
  sudoPassword: z.string().optional(),
})

export const updateRunResultSchema = commandOutcomeSchema.extend({
  rebootRequired: z.boolean(),
})
export type UpdateRunResult = z.infer<typeof updateRunResultSchema>

// ---------------------------------------------------------------- power

export const rebootServerSchema = z.object({
  sudoPassword: z.string().optional(),
})

export const rebootOutcomeSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
})
export type RebootOutcome = z.infer<typeof rebootOutcomeSchema>

// ---------------------------------------------------------------- security

export const firewallRuleSchema = z.object({
  number: z.number().int().nullable(),
  raw: z.string(),
})

export const jailSchema = z.object({
  name: z.string(),
  currentlyFailed: z.number().int().nonnegative().nullable(),
  currentlyBanned: z.number().int().nonnegative().nullable(),
  bannedAddresses: z.array(z.string()),
})

export const sshHardeningSchema = z.object({
  permitRootLogin: z.string().nullable(),
  passwordAuthentication: z.string().nullable(),
  /** True when the value read is the safe one; null when it could not be read. */
  rootLoginSafe: z.boolean().nullable(),
  passwordAuthSafe: z.boolean().nullable(),
})

export const securityOverviewSchema = z.object({
  serverId: z.string(),
  firewall: z
    .object({
      active: z.boolean(),
      rules: z.array(firewallRuleSchema),
    })
    .nullable(),
  fail2ban: z
    .object({
      jails: z.array(jailSchema),
    })
    .nullable(),
  updates: z.object({
    pending: z.number().int().nonnegative().nullable(),
    security: z.number().int().nonnegative().nullable(),
  }),
  failedLogins: z
    .array(z.object({ user: z.string(), from: z.string(), at: z.string() }))
    .nullable(),
  ssh: sshHardeningSchema,
  /** Anything that could not be read, so the page never pretends a section is empty. */
  notes: z.array(z.string()),
})
export type SecurityOverview = z.infer<typeof securityOverviewSchema>

// ---------------------------------------------------------------- backups

export const backupKindSchema = z.enum(['postgres', 'mysql', 'directory'])
export type BackupKind = z.infer<typeof backupKindSchema>

export const backupTargetKindSchema = z.enum(['local', 'sftp'])

export const backupJobSchema = z.object({
  id: z.string(),
  serverId: z.string(),
  name: z.string(),
  kind: backupKindSchema,
  source: z.string(),
  targetKind: backupTargetKindSchema,
  targetPath: z.string(),
  targetServerId: z.string().nullable(),
  keep: z.number().int().positive(),
  scheduleMin: z.number().int().nullable(),
  enabled: z.boolean(),
  lastRunAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type BackupJob = z.infer<typeof backupJobSchema>

export const createBackupJobSchema = z
  .object({
    serverId: z.string().min(1),
    name: z.string().trim().min(1, 'Der Job braucht einen Namen.').max(80),
    kind: backupKindSchema,
    source: z.string().trim().min(1, 'Ohne Quelle gibt es nichts zu sichern.'),
    targetKind: backupTargetKindSchema.default('local'),
    targetPath: z
      .string()
      .trim()
      .min(1, 'Wohin soll das Archiv?')
      .startsWith('/', 'Der Pfad muss mit / beginnen.'),
    targetServerId: z.string().min(1).nullable().default(null),
    keep: z.number().int().min(1).max(365).default(7),
    scheduleMin: z.number().int().min(5).max(10_080).nullable().default(null),
    enabled: z.boolean().default(true),
  })
  .superRefine((value, ctx) => {
    if (value.targetKind === 'sftp' && value.targetServerId === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['targetServerId'],
        message: 'Für ein Ziel per SFTP fehlt der Zielserver.',
      })
    }
    if (value.kind === 'directory' && !value.source.startsWith('/')) {
      ctx.addIssue({
        code: 'custom',
        path: ['source'],
        message: 'Ein Ordner braucht einen absoluten Pfad.',
      })
    }
  })
export type CreateBackupJobInput = z.input<typeof createBackupJobSchema>

export const updateBackupJobSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  source: z.string().trim().min(1).optional(),
  targetPath: z.string().trim().min(1).startsWith('/').optional(),
  keep: z.number().int().min(1).max(365).optional(),
  scheduleMin: z.number().int().min(5).max(10_080).nullable().optional(),
  enabled: z.boolean().optional(),
})

export const backupJobIdParamsSchema = z.object({ id: z.string().min(1) })

export const backupRunSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  status: z.enum(['running', 'ok', 'failed']),
  artifact: z.string().nullable(),
  sizeBytes: z.number().nonnegative().nullable(),
  message: z.string().nullable(),
  /** How many old archives this run cleaned up. */
  removed: z.number().int().nonnegative(),
})
export type BackupRun = z.infer<typeof backupRunSchema>

export const backupRunsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(20),
})
